<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12 full-height-page-wrapper">
        <div class="page-table-header">
          <PageHeader showBreadcrumbs :page-title="'Contractor bank details'">
            <template #buttons>
              <b-button
                class="button title-container__controls-item"
                :variant="isShowFilter ? 'secondary' : 'info'"
                @click="toggleShowFilter"
              >
                <span class="material-icons icon-image-preview">
                  tune
                </span>
                Filters
              </b-button>
              <b-button
                v-if="$can(permissions.CreateFinancesClientBankDetails)"
                class="button"
                variant="success"
                @click.prevent="showCreateModal"
                :disabled="isDataPageLoading"
              >
                Create
              </b-button>
            </template>
          </PageHeader>

          <Filters
            ref="filtersComponent"
            v-show="isShowFilter"
            textButtonSubmit="Filter"
            :data="getFilters"
            :isLoading="isDataPageLoading && isInitialDataLoaded"
            @submit="setFilterBankDetails"
          />
        </div>

        <div v-if="isDataPageLoading && !isInitialDataLoaded" class="content-loading-wrapper">
          <Preloader class="centered-preloader" />
        </div>

        <div v-else class="full-height-content">
          <TableForPage
            v-if="getBankDetailsTableData && getBankDetailsTableData.length"
            ref="table"
            :tableData="getBankDetailsTableData || []"
            :isDataLoading="isDataPageLoading && isInitialDataLoaded"
            :fields="getTableFields"
            :perPage="getBankDetails.per_page"
            :currentPage="getBankDetails.current_page"
            :totalRows="getBankDetails.total"
            :tableRowModalFields="tableRowModal.fields"
            :isEdit="$can(permissions.UpdateAllFinancesContractorBankDetails)"
            :isRemove="$can(permissions.DeleteFinancesContractorBankDetails)"
            isShowView
            isServerRender
            noLocalSorting
            @edit="showEditModal"
            @remove="removeBankDetails"
            @update="updateBankDetails"
          />
          <EmptyStateNoResults
            v-else-if="isInitialDataLoaded && getBankDetailsTableData && !getBankDetailsTableData.length && !getBankDetails.isAnyData"
            customIcon="account_balance"
            customIconClasses="material-symbols-outlined empty-state__icon"
            title="No financial accounts added"
            description="You haven't added any financial accounts. Create one to start tracking transactions and balances."
            buttonText="CREATE"
            @button-click="handleCreateClick"
          />
          <EmptyStateNoResults
            v-else-if="isInitialDataLoaded && getBankDetailsTableData && !getBankDetailsTableData.length && getBankDetails.isAnyData"
            @reset-filters="handleResetFilters"
          />
        </div>
      </div>
    </div>

    <b-modal
      size="lg"
      hide-footer
      v-model="createModal.show"
      class="modal"
      @show="resetCreateModalForm"
      centered
      :modal-class="isCreateModalLoading ? 'loading' : ''"
    >
      <template v-slot:modal-header="{ cancel }">
        <h5 class="modal-title">{{createModal.title}}</h5>
        <button
          type="button"
          aria-label="Close"
          class="close"
          @click.prevent="cancel"
        >
          <span class="material-icons">
            close
          </span>
        </button>
      </template>
      <FormGenerator
        :data="createModal.form"
        :isLoading="isCreateModalLoading"
        @submit="createBankDetails"
        :showReset="false"
        :textButtonSubmit="createModal.submitButtonText"
      >
        <template v-slot:companyName="slotProps">
          <b-form-group v-if="isBusinessEntityCompany(slotProps.fields.business_entity_type)">
            <label>{{slotProps.data.label}}</label>
            <b-form-input
              v-model="slotProps.data.value"
              :required="slotProps.data.required"
              :placeholder="slotProps.data.placeholder"
            />
          </b-form-group>
        </template>
        <template v-slot:companyNameNational="slotProps">
          <b-form-group v-if="isBusinessEntityCompany(slotProps.fields.business_entity_type)">
            <label>{{slotProps.data.label}}</label>
            <b-form-input
              v-model="slotProps.data.value"
              :required="slotProps.data.required"
              :placeholder="slotProps.data.placeholder"
            />
          </b-form-group>
        </template>
      </FormGenerator>
    </b-modal>
    <b-modal
      size="lg"
      hide-footer
      v-model="editModal.show"
      class="modal"
      centered
      :modal-class="isEditModalLoading ? 'loading' : ''"
    >
      <template v-slot:modal-header="{ cancel }">
        <h5 class="modal-title">{{editModal.title}}</h5>
        <button
          type="button"
          aria-label="Close"
          class="close"
          @click.prevent="cancel"
        >
          <span class="material-icons">
            close
          </span>
        </button>
      </template>
      <FormGenerator
        :data="editModal.form"
        :isLoading="isEditModalLoading"
        @submit="editBankDetails"
        :showReset="false"
        :textButtonSubmit="editModal.submitButtonText"
      >
        <template v-slot:companyName="slotProps">
          <b-form-group v-if="isBusinessEntityCompany(slotProps.fields.business_entity_type)">
            <label>{{slotProps.data.label}}</label>
            <b-form-input
              v-model="slotProps.data.value"
              :required="slotProps.data.required"
              :placeholder="slotProps.data.placeholder"
            />
          </b-form-group>
        </template>
        <template v-slot:companyNameNational="slotProps">
          <b-form-group v-if="isBusinessEntityCompany(slotProps.fields.business_entity_type)">
            <label>{{slotProps.data.label}}</label>
            <b-form-input
              v-model="slotProps.data.value"
              :required="slotProps.data.required"
              :placeholder="slotProps.data.placeholder"
            />
          </b-form-group>
        </template>
      </FormGenerator>
    </b-modal>
  </div>
</template>

<script>
import PageHeader from '@components/PageHeader'
import Filters from '@components/Filter/Filter'
import FormGenerator from '@components/FormGenerator'
import TableForPage from '@tables/TableForPage'
import BankDetailsMixin from '../mixins/BankDetailsMixin'
import { mapGetters } from 'vuex'
import Preloader from '@components/Preloader'
import EmptyStateNoResults from '@components/EmptyStateNoResults'

export default {
  name: 'FinanceBankDetailsContractors',
  components: {
    PageHeader,
    Filters,
    TableForPage,
    FormGenerator,
    Preloader,
    EmptyStateNoResults
  },
  mixins: [BankDetailsMixin],
  data () {
    return {
      modalTitle: 'Contractors Bank Details',
      ownershipId: 4,
      isInitialDataLoaded: false,
      isCreateModalLoading: false,
      isEditModalLoading: false,
      currentFields: [
        'name',
        'contractor.name',
        'currency.code',
        'country.name',
        'business_entity_type.type',
        'first_name',
        'last_name',
        'company_name',
        'actions'
      ]
    }
  },
  computed: {
    ...mapGetters({
      getAllUsers: 'getAllUsers'
    }),
    getContractors () {
      return this._getPartnerRelatedUsers(null) || []
    },
    getFilters () {
      const currentFilters = (({
        contractorId,
        currencyId,
        country,
        businessEntityTypeId,
        firstName,
        lastName,
        companyName
      }) => ({
        contractorId,
        currencyId,
        country,
        businessEntityTypeId,
        firstName,
        lastName,
        companyName
      }))(this.filters)

      return currentFilters
    },
    formMock () {
      return {
        'id': {
          name: 'id'
        },
        'contractor': {
          type: 'select',
          name: 'contractor_id',
          label: 'Contractors*',
          selectLabel: 'name',
          valueField: 'id',
          options: [],
          value: null,
          validation: [
            { name: 'required' }
          ],
          class: 'col-12'
        },
        ...this.mainFormMock
      }
    }
  },
  methods: {
    fetchAllData () {
      this.fetchBankDetails()
      Promise.all([
        this.fetchAllContractors(),
        this.fetchBusinessEntityTypes(),
        this.fetchCountries(),
        this.fetchCurrencies()
      ])
        .then(values => {
          this.setAllOptionsFilter()
        })
    },
    setAllOptionsFilter () {
      this.createModal.form.contractor.options = this.getContractors
      this.createModal.form.business_entity_type.options = this.getBusinessEntityTypes
      this.createModal.form.country.options = this.getCountries
      this.createModal.form.currency.options = this.getCurrencies

      this.editModal.form.contractor.options = this.getContractors
      this.editModal.form.business_entity_type.options = this.getBusinessEntityTypes
      this.editModal.form.country.options = this.getCountries
      this.editModal.form.currency.options = this.getCurrencies

      this.filters.contractorId.options = this.getContractors
      this.filters.businessEntityTypeId.options = this.getBusinessEntityTypes
      this.filters.currencyId.options = this.getCurrencies
      this.filters.country.options = this.getCountries

      this.isDataPageLoading = false
      this.isInitialDataLoaded = true
    },
    handleResetFilters () {
      if (this.$refs.filtersComponent) {
        this.$refs.filtersComponent.resetFilter()
      }
    },
    handleCreateClick () {
      this.showCreateModal()
    },
    async createBankDetails (fields) {
      const data = this.prepareDataForEditAndCreateRequest(fields)
      data.ownership_id = this.ownershipId

      this.isCreateModalLoading = true
      try {
        await this.$api.finances.createBankDetails(data)
        this.$snotify.success(
          'Bank details created successfully',
          'Success',
          this.$store.state.snotify
        )
        this.hideCreateModal()
        this.updateBankDetails()
      } catch (error) {
        this.errorNotifications(error)
      } finally {
        this.isCreateModalLoading = false
      }
    },
    async editBankDetails (fields) {
      const data = this.prepareDataForEditAndCreateRequest(fields)
      data.ownership_id = this.ownershipId

      this.isEditModalLoading = true
      try {
        await this.$api.finances.editBankDetails(data)
        this.$snotify.success(
          'Bank details updated successfully',
          'Success',
          this.$store.state.snotify
        )
        this.hideEditModal()
        this.updateBankDetails()
      } catch (error) {
        this.errorNotifications(error)
      } finally {
        this.isEditModalLoading = false
      }
    }
  }
}
</script>
